import {
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from '../lib/utils.js';
import { Skeleton } from './skeleton.js';

/* Table primitives shadcn-style (ADR-0118). Fonte única para as ~15 tabelas do backoffice
   que hoje copiam a mesma marcação com drift (hover accent/10 vs 15, sem tabular-nums). O
   wrapper emoldura em `bg-card` com borda arredondada e scroll horizontal próprio; o header
   é `bg-muted/30` uppercase; a linha tem hover accent/15 canônico. Colunas numéricas usam
   `<TableCell numeric>` (text-right + tabular-nums). */

export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div
      data-slot="table-wrapper"
      className="w-full overflow-x-auto rounded-md border border-border bg-card"
    >
      <table
        ref={ref}
        data-slot="table"
        className={cn('w-full border-collapse text-sm', className)}
        {...props}
      />
    </div>
  ),
);
Table.displayName = 'Table';

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    data-slot="table-header"
    className={cn(
      'border-b border-border bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground',
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} data-slot="table-body" className={cn(className)} {...props} />
));
TableBody.displayName = 'TableBody';

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Linha clicável (drill-down) — aplica cursor-pointer. O hover accent/15 é sempre aplicado. */
  readonly interactive?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, interactive, ...props }, ref) => (
    <tr
      ref={ref}
      data-slot="table-row"
      className={cn(
        'border-b border-border transition-colors last:border-0 hover:bg-accent/15',
        interactive === true && 'cursor-pointer',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

export const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      data-slot="table-head"
      className={cn('px-4 py-3 font-medium', className)}
      {...props}
    />
  ),
);
TableHead.displayName = 'TableHead';

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Coluna numérica — alinha à direita com `tabular-nums` (dígitos de largura fixa). */
  readonly numeric?: boolean;
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, numeric, ...props }, ref) => (
    <td
      ref={ref}
      data-slot="table-cell"
      className={cn(
        'px-4 py-3 align-middle',
        numeric === true && 'text-right tabular-nums',
        className,
      )}
      {...props}
    />
  ),
);
TableCell.displayName = 'TableCell';

export interface TableSkeletonProps {
  /** Nº de linhas fantasma (padrão = página cheia). */
  readonly rows?: number;
  /** Nº de colunas — casa a geometria da tabela real p/ evitar layout shift. */
  readonly columns: number;
}

/* Skeleton com a MESMA moldura/geometria da Table — preserva o layout durante o load em vez
   do "spinner-then-pop". Reusa o wrapper bg-card + linhas com Skeleton por célula. */
export function TableSkeleton({ rows = 8, columns }: TableSkeletonProps) {
  return (
    <div
      data-slot="table-skeleton"
      className="w-full overflow-hidden rounded-md border border-border bg-card"
      aria-hidden="true"
    >
      <table className="w-full border-collapse text-sm">
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton fantasma estático sem identidade
            <tr key={r} className="border-b border-border last:border-0">
              {Array.from({ length: columns }, (_, c) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: idem
                <td key={c} className="px-4 py-3">
                  <Skeleton className="h-4 w-full max-w-[8rem]" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
